import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faDesktop,
  faPaperPlane,
  faServer,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { RichContext } from "@/hooks/useRichAniContext";
import { DeveloperMenuSection } from "./DeveloperMenuSection";
import type { OsAniContextSnapshot } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import { AniFeToolDebugPanel, type FeToolDebugRunner } from "./AniFeToolDebugPanel";
import { AniMcpDebugPanel } from "./AniMcpDebugPanel";
import {
  AgentPayloadContextSection,
  OsContextSection,
  UrlInferredSection,
} from "./developerMenu/contextSections";
import { useAuth } from "@/context/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useFloatingAniProfileCompanyId } from "@/hooks/useFloatingAniProfileCompanyId";
import { fetchResourceTypeCatalog } from "@/services/api/chatDebugMenu";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";

export interface AniDeveloperMenuProps {
  richContext: RichContext;
  contextLabel: string;
  showMcpDebugPanel?: boolean;
  showFeToolDebugPanel?: boolean;
  feToolDebugRunner?: FeToolDebugRunner;
  feToolDebugOsContext?: OsAniContextSnapshot;
  feOpenAppAllowlist?: readonly string[];
  agentPayloadCurrentApp?: string;
  agentPayloadPersonalityMode?: string;
  agentPayloadCompanyId?: string;
}

export function AniDeveloperMenu({
  richContext,
  contextLabel: _contextLabel,
  showMcpDebugPanel = false,
  showFeToolDebugPanel = false,
  feToolDebugRunner,
  feToolDebugOsContext,
  feOpenAppAllowlist,
  agentPayloadCurrentApp,
  agentPayloadPersonalityMode,
  agentPayloadCompanyId,
}: AniDeveloperMenuProps) {
  const { user } = useAuth();
  const isAniStaff = user?.company_id === "ani";
  const isAdmin = useIsAdmin();
  const canUseProfileCompanyOverride = isAniStaff && isAdmin;
  const { profileCompanyId, setProfileCompanyId } = useFloatingAniProfileCompanyId(canUseProfileCompanyOverride);
  const catalogQuery = useQuery({
    queryKey: ["chat-debug-menu", "resource-type-catalog"],
    queryFn: fetchResourceTypeCatalog,
    enabled: canUseProfileCompanyOverride,
    staleTime: 10 * 60 * 1000,
  });
  const profileCompanyOptions = useMemo((): SelectOption<string>[] => {
    const own: SelectOption<string> = { value: "", label: "Own company" };
    const ids = catalogQuery.data?.profile_company_ids ?? [];
    return [own, ...ids.map((id) => ({ value: id, label: id }))];
  }, [catalogQuery.data?.profile_company_ids]);

  const showUrlInferred =
    Boolean(richContext.urlInferredContext) &&
    !richContext.osContext &&
    (!richContext.apiContext || richContext.apiContext.activeResources.length === 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="flex flex-col gap-2 overflow-hidden"
      data-testid="ani-developer-menu"
    >
      <h2 className="px-1 font-mono text-sm uppercase tracking-wide text-slate-400">Developer menu</h2>

      {canUseProfileCompanyOverride ? (
        <div className="px-1" data-testid="ani-profile-company-selector">
          <SelectDropdown
            label="Profile company"
            value={profileCompanyId}
            options={profileCompanyOptions}
            onChange={(next) => setProfileCompanyId(next)}
          />
        </div>
      ) : null}

      {richContext.osContext ? (
        <DeveloperMenuSection
          title="OS Context"
          testId="developer-menu-section-os-context"
          defaultOpen
          headingIcon={<FontAwesomeIcon icon={faDesktop} className="h-3 w-3" aria-hidden="true" />}
        >
          <OsContextSection richContext={richContext} />
        </DeveloperMenuSection>
      ) : null}

      <DeveloperMenuSection
        title="Agent payload (next send)"
        testId="developer-menu-section-agent-payload"
        defaultOpen
        headingIcon={<FontAwesomeIcon icon={faPaperPlane} className="h-3 w-3" aria-hidden="true" />}
      >
        <AgentPayloadContextSection
          richContext={richContext}
          osContext={feToolDebugOsContext}
          currentApp={agentPayloadCurrentApp ?? richContext.osContext?.focusedApp}
          personalityMode={agentPayloadPersonalityMode}
          companyId={agentPayloadCompanyId}
        />
      </DeveloperMenuSection>

      {showUrlInferred ? (
        <DeveloperMenuSection title="URL inferred" testId="developer-menu-section-url-inferred">
          <UrlInferredSection richContext={richContext} />
        </DeveloperMenuSection>
      ) : null}

      <DeveloperMenuSection
        title="MCP Servers"
        testId="developer-menu-section-mcp"
        headingIcon={<FontAwesomeIcon icon={faServer} className="h-3 w-3" aria-hidden="true" />}
      >
        <AniMcpDebugPanel enabled={showMcpDebugPanel} embedded />
      </DeveloperMenuSection>

      <DeveloperMenuSection
        title="Frontend tools"
        testId="developer-menu-section-frontend-tools"
        headingIcon={<FontAwesomeIcon icon={faBolt} className="h-3 w-3" aria-hidden="true" />}
      >
        {feToolDebugRunner ? (
          <AniFeToolDebugPanel
            enabled={showFeToolDebugPanel}
            runTool={feToolDebugRunner}
            osContext={feToolDebugOsContext}
            feOpenAppAllowlist={feOpenAppAllowlist}
            embedded
          />
        ) : (
          <p className="text-xs text-slate-500">FE tool runner not available in this surface.</p>
        )}
      </DeveloperMenuSection>
    </motion.div>
  );
}
